import { zodResolver } from '@hookform/resolvers/zod'
import { useMemo } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { useRequestRevisionChecklist } from '@/api/checklists/request-revision-checklist'
import { TextAreaFormField } from '@/components/form/text-area-form-field/text-area-form-field'
import { ConfirmationPopup } from '@/components/ui/confirmation-popup'
import { Typography } from '@/components/ui/typography'

import * as S from './request-revision-popup.styled'
import { makeRevisionRequestSchema, RevisionRequestFormData } from './request-revision-popup.types'

export interface RequestRevisionPopupProps {
  onClose: () => void
  checklistId: number
}

export const RequestRevisionPopup = ({ onClose, checklistId }: RequestRevisionPopupProps) => {
  const { t } = useTranslation(['checklists', 'common'])
  const { mutateAsync: requestRevisionChecklist, isPending } = useRequestRevisionChecklist()

  const revisionRequestSchema = useMemo(() => makeRevisionRequestSchema(t), [t])

  const form = useForm<RevisionRequestFormData>({
    mode: 'onSubmit',
    resolver: zodResolver(revisionRequestSchema),
    values: {
      revisionRequestNote: '',
    },
  })

  const { handleSubmit } = form

  const onSubmit = async (values: RevisionRequestFormData) => {
    await requestRevisionChecklist({
      checklistId,
      revisionRequestNote: values.revisionRequestNote,
    })
    onClose()
  }

  return (
    <ConfirmationPopup
      title={t('checklists:requestRevisionPopup.title')}
      confirmText={t('checklists:requestRevisionPopup.confirmText')}
      cancelText={t('common:actions.cancel')}
      variant="warning"
      autoClose={false}
      onConfirm={handleSubmit(onSubmit)}
      onClose={onClose}
    >
      <FormProvider {...form}>
        <S.Body>
          <TextAreaFormField
            label={t('checklists:requestRevisionPopup.note.label')}
            placeholder={t('checklists:requestRevisionPopup.note.placeholder')}
            maxLength={220}
            disabled={isPending}
            name="revisionRequestNote"
          />
          <Typography size="text-small" color="black400">
            {t('checklists:requestRevisionPopup.note.validation.maxLength')}
          </Typography>
        </S.Body>
      </FormProvider>
    </ConfirmationPopup>
  )
}
